'use client';

import type {
  BodyScrollEndEvent,
  CellClickedEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  RowClassParams,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { Box, HStack, token } from '@terra/ui-styled-system';
import { Button, Splitter, Table } from '@terra/ui-design-system';
import { ArrowLeft01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import type { HARRequest } from '../../hooks/api/mapHarEntryToHarRequest';
import { useLayoutStore } from '../../store/layoutStore';
import { RequestDetailsPanel } from './RequestDetailsPanel';

type RequestDetailsPanelProps = ComponentProps<typeof RequestDetailsPanel>;

const FULL_PANEL_SIZE = 100;

type RequestsContentGridProps = {
  columnDefs: ColDef<HARRequest>[];
  rowData: HARRequest[];
  enableSelection: boolean;
  selectedSlimRequest: HARRequest | null;
  selectedFullRequest: HARRequest | null;
  isLoadingFullHar: boolean;
  isFullHarError: boolean;
  applicationId?: string;
  tablePersistence: ComponentProps<typeof Table<HARRequest>>['persistence'];
  onGridReady: (api: GridApi<HARRequest>) => void;
  onCellClicked: (event: CellClickedEvent<HARRequest>) => void;
  onSelectionChanged: (event: SelectionChangedEvent<HARRequest>) => void;
  onBodyScrollEnd: (event: BodyScrollEndEvent<HARRequest>) => void;
  onCloseDetails: () => void;
  onReplay: (request: HARRequest) => void;
  openDecoderWith: RequestDetailsPanelProps['openDecoderWith'];
  addRequestToComparer: RequestDetailsPanelProps['addRequestToComparer'];
  appendToInput: RequestDetailsPanelProps['appendToInput'];
  sendHttpMentionsToChat: RequestDetailsPanelProps['sendHttpMentionsToChat'];
};

export function RequestsContentGrid({
  columnDefs,
  rowData,
  enableSelection,
  selectedSlimRequest,
  selectedFullRequest,
  isLoadingFullHar,
  isFullHarError,
  applicationId,
  tablePersistence,
  onGridReady,
  onCellClicked,
  onSelectionChanged,
  onBodyScrollEnd,
  onCloseDetails,
  onReplay,
  openDecoderWith,
  addRequestToComparer,
  appendToInput,
  sendHttpMentionsToChat,
}: RequestsContentGridProps) {
  const tCommon = useTranslations('common');
  const requestsContentSplitterSizes = useLayoutStore(
    (state) => state.requestsContentSplitterSizes,
  );
  const setRequestsContentSplitterSizes = useLayoutStore(
    (state) => state.setRequestsContentSplitterSizes,
  );
  const isDetailsOpen = Boolean(selectedSlimRequest);
  const persistRequestsContentSplitterSizes = ({
    size,
  }: {
    size: number[];
  }) => {
    setRequestsContentSplitterSizes(
      requestsContentSplitterSizes.map((panel, index) => {
        const nextSize = size[index];

        return {
          ...panel,
          size: nextSize ?? panel.size,
        };
      }),
    );
  };

  const getRowId = (params: GetRowIdParams<HARRequest>) => params.data.id;
  const getRowStyle = (
    params: RowClassParams<HARRequest>,
  ): {
    cursor: string;
    background?: string;
  } => {
    if (params.data && selectedSlimRequest?.id === params.data.id) {
      return {
        cursor: 'pointer',
        background: token('colors.bg.subtle'),
      };
    }

    return { cursor: 'pointer' };
  };

  return (
    <>
      {isDetailsOpen ? (
        <HStack
          w="full"
          px="2"
          py="1"
          borderBottomWidth="1px"
          borderColor="border.subtle"
          bg="bg.subtle"
          flexShrink={0}
        >
          <Button variant="ghost" size="xs" onClick={onCloseDetails}>
            <ArrowLeft01Icon size={14} />
            {tCommon('back')}
          </Button>
        </HStack>
      ) : null}
      <Splitter.Root
        orientation="vertical"
        flex="1"
        w="full"
        minH="0"
        defaultSize={
          isDetailsOpen
            ? requestsContentSplitterSizes.map(({ size }) => size)
            : undefined
        }
        size={
          isDetailsOpen
            ? requestsContentSplitterSizes.map(({ size }) => size)
            : [FULL_PANEL_SIZE]
        }
        panels={
          isDetailsOpen
            ? requestsContentSplitterSizes.map(({ id }) => ({
                id: id.toString(),
              }))
            : [{ id: 'table' }]
        }
        onResize={
          isDetailsOpen ? persistRequestsContentSplitterSizes : undefined
        }
        onResizeEnd={
          isDetailsOpen ? persistRequestsContentSplitterSizes : undefined
        }
        borderWidth="0"
        gap={0}
      >
        <Splitter.Panel id="table" borderWidth="0" w="100%" h="100%">
          <Box h="full" w="full" overflow="hidden">
            <Table
              columnDefs={columnDefs}
              rowData={rowData}
              rowHeight={40}
              headerHeight={32}
              noBorder={true}
              fontSize={12}
              headerFontSize={11}
              animateRows={false}
              rowBuffer={100}
              debounceVerticalScrollbar={true}
              defaultColDef={{
                sortable: false,
                filter: false,
                resizable: true,
              }}
              persistence={tablePersistence}
              hideColumnControls={true}
              fitColumnsOnGridSizeChanged={false}
              suppressCellFocus={true}
              getRowId={getRowId}
              getRowStyle={getRowStyle}
              rowSelection={enableSelection ? 'multiple' : undefined}
              suppressRowClickSelection={enableSelection}
              onGridReady={(event) => onGridReady(event.api)}
              onCellClicked={onCellClicked}
              onSelectionChanged={onSelectionChanged}
              onBodyScrollEnd={onBodyScrollEnd}
            />
          </Box>
        </Splitter.Panel>
        {isDetailsOpen ? (
          <>
            <Splitter.ResizeTrigger
              id="table:details"
              height="4px"
              minHeight="4px"
              bg="transparent"
              position="relative"
              cursor="row-resize"
              zIndex={10}
              _after={{
                content: '""',
                position: 'absolute',
                inset: '-4px 0 -4px 0',
                height: '12px',
                bg: 'transparent',
                cursor: 'row-resize',
                zIndex: 10,
              }}
              _hover={{
                bg: 'border.default',
                _after: {
                  bg: 'border.default',
                },
              }}
            />
            <Splitter.Panel id="details" borderWidth="0" h="100%" w="100%">
              <RequestDetailsPanel
                applicationId={applicationId}
                selectedRequest={selectedFullRequest}
                isLoadingFullHar={isLoadingFullHar}
                isFullHarError={isFullHarError}
                onReplay={onReplay}
                openDecoderWith={openDecoderWith}
                addRequestToComparer={addRequestToComparer}
                appendToInput={appendToInput}
                sendHttpMentionsToChat={sendHttpMentionsToChat}
              />
            </Splitter.Panel>
          </>
        ) : null}
      </Splitter.Root>
    </>
  );
}
